import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, message } from 'antd';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { investorApi, productApi } from '../../services/privateFundApi';
import './PrivateFundHome.css';

/**
 * 私募产品管理主页面 - 投资人员视角
 * 以投资人员为主视角，重点显示"铖"和"国英"的投资情况
 * 
 * @returns {JSX.Element} 主页面组件
 */
const PrivateFundHome = () => {
  const navigate = useNavigate();
  const [allInvestors, setAllInvestors] = useState([]); // 所有投资人员列表
  const [selectedInvestorIds, setSelectedInvestorIds] = useState([]); // 选中的投资人员ID列表（从localStorage读取）
  const [selectedInvestors, setSelectedInvestors] = useState([]); // 重点关注的投资者
  const [investorDetails, setInvestorDetails] = useState({}); // 投资人员详情（包含产品详情）
  const [products, setProducts] = useState([]); // 产品列表
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInvestorSelector, setShowInvestorSelector] = useState(false); // 显示投资人员选择对话框
  const [currentUser, setCurrentUser] = useState(null); // 当前登录用户
  const [isAdmin, setIsAdmin] = useState(false); // 是否为管理员

  // 获取投资人员详情（包含产品详情）
  const fetchInvestorDetail = useCallback(async (investorId) => {
    try {
      const response = await investorApi.getInvestorById(investorId);
      if (response && response.success) {
        setInvestorDetails(prev => ({
          ...prev,
          [investorId]: response.data
        }));
      }
    } catch (err) {
      console.error(`获取投资人员详情失败 (${investorId}):`, err);
    }
  }, []);

  // 从localStorage加载选中的投资人员ID（只返回数据，不设置状态）
  const loadSelectedInvestorIds = useCallback(() => {
    try {
      const saved = localStorage.getItem('privateFundSelectedInvestorIds');
      if (saved) {
        const ids = JSON.parse(saved);
        return ids;
      }
      // 如果没有保存的选择，返回空数组
      return [];
    } catch (err) {
      console.error('加载选中的投资人员失败:', err);
      return [];
    }
  }, []);

  // 保存选中的投资人员ID到localStorage
  const saveSelectedInvestorIds = useCallback((ids) => {
    try {
      localStorage.setItem('privateFundSelectedInvestorIds', JSON.stringify(ids));
      setSelectedInvestorIds(ids);
    } catch (err) {
      console.error('保存选中的投资人员失败:', err);
    }
  }, []);

  // 获取投资人员列表
  const fetchInvestors = useCallback(async () => {
    try {
      const response = await investorApi.getInvestors();
      console.log('获取投资人员列表响应:', response);
      
      if (response && response.success) {
        const investorsList = response.data || [];
        setAllInvestors(investorsList);
        
        // 从localStorage加载选中的投资人员ID
        const savedIds = loadSelectedInvestorIds();
        
        // 如果没有保存的选择，默认选择"铖"和"国英"
        let idsToShow = savedIds;
        if (idsToShow.length === 0) {
          const defaultInvestors = investorsList.filter(inv => 
            inv.name === '铖' || inv.name === '国英'
          );
          idsToShow = defaultInvestors.map(inv => inv._id);
          saveSelectedInvestorIds(idsToShow);
        }
        
        // 根据选中的ID筛选投资人员
        const focusInvestors = investorsList.filter(inv => idsToShow.includes(inv._id));
        setSelectedInvestors(focusInvestors);
        
        // 并行获取每个投资人员的详情（包含产品信息）
        await Promise.all(
          focusInvestors.map(investor => fetchInvestorDetail(investor._id))
        );
        
        console.log(`成功获取 ${investorsList.length} 位投资人员数据`);
      }
    } catch (err) {
      console.error('获取投资人员列表失败:', err);
    }
  }, [fetchInvestorDetail, loadSelectedInvestorIds, saveSelectedInvestorIds]);

  // 获取产品列表（包含详情，用于获取赎回记录）
  const fetchProducts = useCallback(async () => {
    try {
      const response = await productApi.getProducts();
      console.log('获取产品列表响应:', response);
      
      if (response && response.success) {
        const productsList = response.data || [];
        
        // 对于已赎回的产品，获取详情以获取赎回记录
        const productsWithDetails = await Promise.all(
          productsList.map(async (product) => {
            if (product.status === 'redeemed') {
              try {
                const detailResponse = await productApi.getProductById(product._id);
                if (detailResponse && detailResponse.success && detailResponse.data) {
                  return {
                    ...product,
                    redemptionRecord: detailResponse.data.redemptionRecord
                  };
                }
              } catch (err) {
                console.error(`获取产品详情失败 (${product._id}):`, err);
              }
            }
            return product;
          })
        );
        
        setProducts(productsWithDetails);
        console.log(`成功获取 ${productsWithDetails.length} 条产品数据`);
      }
    } catch (err) {
      console.error('获取产品列表失败:', err);
    }
  }, []);

  // 加载所有数据
  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([fetchInvestors(), fetchProducts()]);
    } catch (err) {
      console.error('加载数据失败:', err);
      setError(err.message || '加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [fetchInvestors, fetchProducts]);

  // 获取当前用户信息
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        setIsAdmin(user.isAdmin === true);
      }
    } catch (error) {
      console.error('获取当前用户信息失败:', error);
    }
  }, []);

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('已退出登录');
    navigate('/login', { replace: true });
  };

  // 初始化：从localStorage加载选中的投资人员ID
  useEffect(() => {
    const savedIds = loadSelectedInvestorIds();
    if (savedIds.length > 0) {
      setSelectedInvestorIds(savedIds);
    }
  }, [loadSelectedInvestorIds]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleBack = () => {
    navigate('/');
  };

  const handleInvestorClick = (investorId) => {
    navigate(`/private-fund/investors/${investorId}`);
  };

  const handleProductClick = (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    navigate(`/private-fund/products/${productId}`);
  };

  const handleAddProduct = () => {
    navigate('/private-fund/products/new');
  };

  // 处理选择投资人员
  const handleSelectInvestors = () => {
    // 使用当前选中的投资人员ID初始化对话框
    const currentIds = selectedInvestors.map(inv => inv._id);
    setSelectedInvestorIds(currentIds);
    setShowInvestorSelector(true);
  };

  // 处理保存选中的投资人员
  const handleSaveSelectedInvestors = () => {
    const selectedIds = selectedInvestorIds;
    saveSelectedInvestorIds(selectedIds);
    
    // 根据选中的ID筛选投资人员
    const focusInvestors = allInvestors.filter(inv => selectedIds.includes(inv._id));
    setSelectedInvestors(focusInvestors);
    
    // 重新获取选中投资人员的详情
    Promise.all(
      focusInvestors.map(investor => fetchInvestorDetail(investor._id))
    ).then(() => {
      setShowInvestorSelector(false);
    });
  };

  // 处理切换投资人员选择状态
  const handleToggleInvestor = (investorId) => {
    setSelectedInvestorIds(prev => {
      if (prev.includes(investorId)) {
        return prev.filter(id => id !== investorId);
      } else {
        return [...prev, investorId];
      }
    });
  };

  // 取消选择投资人员
  const handleCancelSelectInvestors = () => {
    // 恢复当前显示的投资人员选择
    const currentIds = selectedInvestors.map(inv => inv._id);
    setSelectedInvestorIds(currentIds);
    setShowInvestorSelector(false);
  };

  return (
    <div className="private-fund-home">
      <div className="private-fund-header">
        <button className="back-button" onClick={handleBack}>
          ← 返回
        </button>
        <h1 className="private-fund-title">个人投资管理</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isAdmin && (
            <Button 
              icon={<UserOutlined />}
              onClick={() => navigate('/knowledge-lottery/manage?tab=users')}
              title="用户管理"
            >
              用户管理
            </Button>
          )}
          {currentUser && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#666', fontSize: '14px' }}>
                {currentUser.name || currentUser.email}
              </span>
              <Button 
                icon={<LogoutOutlined />}
                danger
                size="small"
                onClick={handleLogout}
              >
                退出
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="private-fund-content">
        {loading && (
          <div className="loading-message">
            <p>加载中...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            <p>错误: {error}</p>
            <button onClick={fetchAllData}>重试</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* 上面：选中的投资者的投资情况 */}
            {selectedInvestors.length > 0 && (
              <div className="investors-section">
                <div className="section-header-with-button">
                  <h2 className="section-title">我的投资</h2>
                  <button 
                    className="action-button small"
                    onClick={handleSelectInvestors}
                  >
                    选择投资人员
                  </button>
                </div>
                <div className="investors-list">
                  {selectedInvestors.map((investor) => {
                    // 计算当前持有产品数和赎回产品数
                    const detail = investorDetails[investor._id];
                    let holdingCount = 0;
                    let redeemedCount = 0;
                    let redeemedProfitLoss = 0; // 已赎回产品的总收益
                    let redeemedInvestmentAmount = 0; // 已赎回产品的总投资额
                    
                    if (detail && detail.productDetails) {
                      detail.productDetails.forEach(productDetail => {
                        const product = products.find(p => p._id === productDetail.productId);
                        if (product) {
                          if (product.status === 'holding') {
                            holdingCount++;
                          } else if (product.status === 'redeemed') {
                            redeemedCount++;
                            // 累加已赎回产品的收益和投资额
                            redeemedProfitLoss += productDetail.profitLoss || 0;
                            redeemedInvestmentAmount += productDetail.investmentAmount || 0;
                          }
                        }
                      });
                    }
                    
                    // 计算已赎回产品的收益比例
                    const redeemedProfitLossRatio = redeemedInvestmentAmount > 0 
                      ? redeemedProfitLoss / redeemedInvestmentAmount 
                      : 0;
                    
                    return (
                    <div 
                      key={investor._id} 
                      className="investor-card"
                      onClick={() => handleInvestorClick(investor._id)}
                    >
                      <div className="investor-header">
                        <h3 className="investor-name">{investor.name}</h3>
                          <div className="investor-product-counts">
                            <span className="product-count-item">
                              持有 {holdingCount} 个
                            </span>
                            <span className="product-count-item">
                              赎回 {redeemedCount} 个
                        </span>
                          </div>
                      </div>
                      
                      <div className="investor-stats">
                        <div className="stat-item">
                          <span className="stat-label">总投资:</span>
                          <span className="stat-value">
                            {investor.totalInvestment?.toLocaleString() || 0} 元
                          </span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">总盈亏:</span>
                          <span className={`stat-value ${investor.totalProfitLoss >= 0 ? 'profit' : 'loss'}`}>
                            {investor.totalProfitLoss >= 0 ? '+' : ''}
                            {investor.totalProfitLoss?.toLocaleString() || 0} 元
                          </span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">盈亏比例:</span>
                          <span className={`stat-value ${investor.profitLossRatio >= 0 ? 'profit' : 'loss'}`}>
                            {investor.profitLossRatio >= 0 ? '+' : ''}
                            {((investor.profitLossRatio || 0) * 100).toFixed(2)}%
                          </span>
                        </div>
                        {redeemedCount > 0 && (
                          <div className="stat-item">
                            <span className="stat-label">已赎回收益:</span>
                            <span className={`stat-value ${redeemedProfitLoss >= 0 ? 'profit' : 'loss'}`}>
                              {redeemedProfitLoss >= 0 ? '+' : ''}
                              {redeemedProfitLoss.toLocaleString()} 元
                              ({redeemedProfitLossRatio >= 0 ? '+' : ''}{(redeemedProfitLossRatio * 100).toFixed(2)}%)
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 操作按钮 */}
            <div className="actions-section">
              <button className="action-button primary" onClick={handleAddProduct}>
                + 添加产品
              </button>
            </div>

            {/* 下面：产品列表 */}
            <div className="products-section">
              <h2 className="section-title">产品列表</h2>
              {products.length === 0 ? (
                <div className="empty-message">
                  <p>暂无产品数据</p>
                  <button className="action-button" onClick={handleAddProduct}>
                    添加第一个产品
                  </button>
                </div>
              ) : (
                <div className="products-list">
                  {products.map((product) => {
                    // 计算产品总盈亏
                    let totalProfitLoss = 0;
                    let totalProfitLossRatio = 0;
                    if (product.status === 'holding' && product.latestNetValue) {
                      // 持有中：根据最新净值计算浮盈/浮亏
                      totalProfitLoss = ((product.latestNetValue - product.purchaseNetValue) / product.purchaseNetValue) * product.purchaseAmount;
                      totalProfitLossRatio = totalProfitLoss / product.purchaseAmount;
                    } else if (product.status === 'redeemed' && product.redemptionRecord) {
                      // 已赎回：从赎回记录获取最终盈亏
                      totalProfitLoss = product.redemptionRecord.profitLoss || 0;
                      totalProfitLossRatio = product.redemptionRecord.profitLossRatio || 0;
                    }
                    
                    // 获取当前用户（铖和国英）在该产品中的投资信息
                    // 确保铖和国英都有记录，没有投资则显示0
                    const investorProductInfoMap = {};
                    
                    // 初始化铖和国英的投资信息
                    selectedInvestors.forEach(investor => {
                      investorProductInfoMap[investor.name] = {
                        name: investor.name,
                        investmentAmount: 0,
                        investmentRatio: 0,
                        profitLoss: 0,
                        profitLossRatio: 0
                      };
                    });
                    
                    // 填充实际投资数据
                    selectedInvestors.forEach(investor => {
                      const detail = investorDetails[investor._id];
                      if (detail && detail.productDetails) {
                        const productDetail = detail.productDetails.find(pd => pd.productId === product._id);
                        if (productDetail) {
                          investorProductInfoMap[investor.name] = {
                            name: investor.name,
                            investmentAmount: productDetail.investmentAmount || 0,
                            investmentRatio: productDetail.investmentRatio || 0,
                            profitLoss: productDetail.profitLoss || 0,
                            profitLossRatio: productDetail.profitLossRatio || 0
                          };
                        }
                      }
                    });
                    
                    // 转换为数组，按固定顺序：铖、国英
                    const investorProductInfo = selectedInvestors.map(investor => 
                      investorProductInfoMap[investor.name] || {
                        name: investor.name,
                        investmentAmount: 0,
                        investmentRatio: 0,
                        profitLoss: 0,
                        profitLossRatio: 0
                      }
                    );
                    
                    return (
                      <div 
                        key={product._id} 
                        className="product-card"
                        onClick={() => handleProductClick(product._id)}
                      >
                        <div className="product-header">
                          <h3 className="product-name">{product.name}</h3>
                          <span className={`status-badge ${product.status === 'holding' ? 'holding' : 'redeemed'}`}>
                            {product.status === 'holding' ? '持有' : '已赎回'}
                          </span>
                        </div>
                        <div className="product-info">
                          <div className="info-row">
                            <span className="info-label">申购时间:</span>
                            <span className="info-value">{new Date(product.purchaseTime).toLocaleDateString()}</span>
                          </div>
                          <div className="info-row">
                            <span className="info-label">总收益:</span>
                            <span className={`info-value ${totalProfitLoss >= 0 ? 'profit' : 'loss'}`}>
                              {totalProfitLoss >= 0 ? '+' : ''}{totalProfitLoss.toLocaleString()} 元
                              ({totalProfitLossRatio >= 0 ? '+' : ''}{(totalProfitLossRatio * 100).toFixed(2)}%)
                            </span>
                          </div>
                          {/* 始终显示铖和国英的投资信息，没有投资则显示0 */}
                          {investorProductInfo.map((info, index) => (
                            <React.Fragment key={index}>
                              <div className="info-row">
                                <span className="info-label">{info.name}投资:</span>
                                <span className="info-value">{info.investmentAmount.toLocaleString()} 元</span>
                              </div>
                              <div className="info-row">
                                <span className="info-label">{info.name}收益:</span>
                                <span className={`info-value ${info.profitLoss >= 0 ? 'profit' : 'loss'}`}>
                                  {info.profitLoss >= 0 ? '+' : ''}{info.profitLoss.toLocaleString()} 元
                                  ({info.profitLossRatio >= 0 ? '+' : ''}{(info.profitLossRatio * 100).toFixed(2)}%)
                                </span>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* 投资人员选择对话框 */}
      {showInvestorSelector && (
        <div className="investor-selector-overlay" onClick={handleCancelSelectInvestors}>
          <div className="investor-selector-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="investor-selector-header">
              <h3>选择要展示的投资人员</h3>
              <button className="close-button" onClick={handleCancelSelectInvestors}>×</button>
            </div>
            <div className="investor-selector-content">
              {allInvestors.length === 0 ? (
                <div className="empty-message">暂无投资人员</div>
              ) : (
                <div className="investor-selector-list">
                  {allInvestors.map((investor) => (
                    <label 
                      key={investor._id} 
                      className="investor-selector-item"
                    >
                      <input
                        type="checkbox"
                        checked={selectedInvestorIds.includes(investor._id)}
                        onChange={() => handleToggleInvestor(investor._id)}
                      />
                      <span className="investor-selector-name">{investor.name}</span>
                      <span className="investor-selector-info">
                        {investor.productCount || 0} 个产品
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="investor-selector-actions">
              <button 
                className="cancel-button" 
                onClick={handleCancelSelectInvestors}
              >
                取消
              </button>
              <button 
                className="submit-button" 
                onClick={handleSaveSelectedInvestors}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateFundHome;

